import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Plus, Edit, Trash2, GripVertical } from 'lucide-react';
import { useSpecificationFieldDefinitions, FieldType } from '@/hooks/useSpecificationFieldDefinitions';
import { useSpecificationCategories } from '@/hooks/useSpecificationCategories';
import { useProductCategories } from '@/hooks/useProductCategories';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Switch } from '@/components/ui/switch';

const FIELD_TYPES: { value: FieldType; label: string }[] = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'textarea', label: 'Text Area' },
  { value: 'dropdown', label: 'Dropdown' },
  { value: 'multiselect', label: 'Multi-Select' },
  { value: 'date', label: 'Date' },
  { value: 'boolean', label: 'Yes/No' },
  { value: 'file_upload', label: 'File Upload' },
];

export function SpecificationFieldsManager() {
  const { categories: productCategories } = useProductCategories();
  const { categories } = useSpecificationCategories();
  const [selectedProductCategoryId, setSelectedProductCategoryId] = useState<string>('');
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');
  const { fields, loading, createField, updateField, deleteField } = useSpecificationFieldDefinitions({
    categoryId: selectedCategoryId || undefined,
    includeInactive: true,
  });

  // Filter specification categories by selected product category
  const filteredCategories = selectedProductCategoryId
    ? categories.filter((cat) => cat.product_category_id === selectedProductCategoryId)
    : categories;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingField, setEditingField] = useState<any>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // State for managing dropdown/multiselect options
  const [allowedValues, setAllowedValues] = useState<string[]>([]);
  const [newOptionInput, setNewOptionInput] = useState('');

  const [formData, setFormData] = useState({
    category_id: '',
    field_name: '',
    field_label: '',
    field_type: 'text' as FieldType,
    is_required: false,
    validation_rules: {},
    default_value: '',
    help_text: '',
    unit_of_measure: '',
    display_order: fields.length + 1,
    is_active: true,
  });

  const resetForm = () => {
    setFormData({
      category_id: selectedCategoryId || '',
      field_name: '',
      field_label: '',
      field_type: 'text',
      is_required: false,
      validation_rules: {},
      default_value: '',
      help_text: '',
      unit_of_measure: '',
      display_order: fields.length + 1,
      is_active: true,
    });
    setAllowedValues([]);
    setNewOptionInput('');
    setEditingField(null);
  };

  const handleOpenDialog = (field?: any) => {
    if (field) {
      setEditingField(field);
      setFormData({
        category_id: field.category_id,
        field_name: field.field_name,
        field_label: field.field_label,
        field_type: field.field_type,
        is_required: field.is_required,
        validation_rules: field.validation_rules || {},
        default_value: field.default_value || '',
        help_text: field.help_text || '',
        unit_of_measure: field.unit_of_measure || '',
        display_order: field.display_order,
        is_active: field.is_active,
      });
      // Initialize allowed values from existing field
      if (field.validation_rules?.allowed_values && Array.isArray(field.validation_rules.allowed_values)) {
        setAllowedValues(field.validation_rules.allowed_values);
      } else {
        setAllowedValues([]);
      }
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setTimeout(resetForm, 200);
  };

  const handleSubmit = async () => {
    try {
      // Prepare data with allowed_values for dropdown/multiselect
      const dataToSave = {
        ...formData,
        validation_rules: (formData.field_type === 'dropdown' || formData.field_type === 'multiselect')
          ? { ...formData.validation_rules, allowed_values: allowedValues }
          : formData.validation_rules
      };

      if (editingField) {
        await updateField(editingField.id, dataToSave);
      } else {
        await createField(dataToSave);
      }
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving field:', error);
    }
  };

  const handleDelete = async () => {
    if (deletingId) {
      try {
        await deleteField(deletingId);
        setDeleteDialogOpen(false);
        setDeletingId(null);
      } catch (error) {
        console.error('Error deleting field:', error);
      }
    }
  };

  // Helper functions for managing options
  const addOption = () => {
    const trimmedValue = newOptionInput.trim();
    if (trimmedValue && !allowedValues.includes(trimmedValue)) {
      setAllowedValues([...allowedValues, trimmedValue]);
      setNewOptionInput('');
    }
  };

  const removeOption = (index: number) => {
    setAllowedValues(allowedValues.filter((_, i) => i !== index));
  };

  const selectedCategory = categories.find(c => c.id === selectedCategoryId);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Specification Field Definitions</h2>
          <p className="text-muted-foreground">
            Define structured fields within each specification category
          </p>
        </div>
        <Button onClick={() => handleOpenDialog()} disabled={!selectedCategoryId}>
          <Plus className="w-4 h-4 mr-2" />
          New Field
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label>Filter by Product Category</Label>
          <Select 
            value={selectedProductCategoryId || undefined} 
            onValueChange={(value) => {
              setSelectedProductCategoryId(value || '');
              setSelectedCategoryId('');
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="All Product Categories" />
            </SelectTrigger>
            <SelectContent>
              {productCategories.map((cat) => (
                <SelectItem key={cat.id} value={cat.id}>
                  {cat.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label>Filter by Specification Category</Label>
          <Select value={selectedCategoryId || undefined} onValueChange={(value) => setSelectedCategoryId(value || '')}>
            <SelectTrigger>
              <SelectValue placeholder="All Spec Categories" />
            </SelectTrigger>
              <SelectContent>
                {filteredCategories.map((cat) => {
                  const productCategory = productCategories.find(pc => pc.id === cat.product_category_id);
                  return (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.name}
                      {productCategory && (
                        <span className="text-xs text-muted-foreground ml-2">
                          ({productCategory.name})
                        </span>
                      )}
                    </SelectItem>
                  );
                })}
              </SelectContent>
          </Select>
        </div>
      </div>

      {selectedCategory && (
        <Card>
          <CardHeader>
            <CardTitle>{selectedCategory.name}</CardTitle>
            <CardDescription>{selectedCategory.description}</CardDescription>
          </CardHeader>
        </Card>
      )}

      {loading ? (
        <div className="p-8">Loading fields...</div>
      ) : (
        <div className="grid gap-4">
          {fields.map((field) => {
            const category = categories.find((cat) => cat.id === field.category_id);
            const productCategory = category?.product_category_id
              ? productCategories.find((pc) => pc.id === category.product_category_id)
              : null;
            return (
            <Card key={field.id}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <div className="flex items-center gap-3">
                  <GripVertical className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      {field.field_label}
                      {field.is_required && (
                        <Badge variant="secondary">Required</Badge>
                      )}
                      {!field.is_active && (
                        <Badge variant="secondary">Inactive</Badge>
                      )}
                    </CardTitle>
                    <CardDescription className="mt-1">
                      {productCategory && (
                        <span className="text-xs text-muted-foreground">
                          {productCategory.name} &gt;{' '}
                        </span>
                      )}
                      <span className="font-medium">{category?.name || 'Unknown Category'}</span> •{' '}
                      <span className="text-xs">{field.field_name}</span>
                    </CardDescription>
                    {field.help_text && (
                      <p className="text-xs text-muted-foreground mt-1">{field.help_text}</p>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleOpenDialog(field)}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setDeletingId(field.id);
                      setDeleteDialogOpen(true);
                    }}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                  <span>Field Name: {field.field_name}</span>
                  <span>Type: {FIELD_TYPES.find(t => t.value === field.field_type)?.label}</span>
                  <span>Order: {field.display_order}</span>
                  {field.unit_of_measure && <span>Unit: {field.unit_of_measure}</span>}
                </div>
              </CardContent>
            </Card>
          );
          })}

          {fields.length === 0 && selectedCategoryId && (
            <Card>
              <CardContent className="pt-6 text-center text-muted-foreground">
                No fields defined for this category yet.
              </CardContent>
            </Card>
          )}

          {!selectedCategoryId && (
            <Card>
              <CardContent className="pt-6 text-center text-muted-foreground">
                Select a specification category to view and manage its fields.
              </CardContent>
            </Card>
          )}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={handleCloseDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingField ? 'Edit Field Definition' : 'Create Field Definition'}
            </DialogTitle>
            <DialogDescription>
              {editingField
                ? 'Update the field definition details below'
                : 'Create a new field definition for specifications'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            <div>
              <Label htmlFor="category">Category *</Label>
              <Select
                value={formData.category_id}
                onValueChange={(value) => setFormData({ ...formData, category_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => {
                        const productCategory = productCategories.find(pc => pc.id === cat.product_category_id);
                        return (
                          <SelectItem key={cat.id} value={cat.id}>
                            {cat.name}
                            {productCategory && (
                              <span className="text-xs text-muted-foreground ml-2">
                                ({productCategory.name})
                              </span>
                            )}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="field_name">Field Name (internal) *</Label>
                <Input
                  id="field_name"
                  value={formData.field_name}
                  onChange={(e) => setFormData({ ...formData, field_name: e.target.value })}
                  placeholder="e.g., paper_weight"
                />
              </div>

              <div>
                <Label htmlFor="field_label">Field Label (display) *</Label>
                <Input
                  id="field_label"
                  value={formData.field_label}
                  onChange={(e) => setFormData({ ...formData, field_label: e.target.value })}
                  placeholder="e.g., Paper Weight"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="field_type">Field Type *</Label>
                <Select
                  value={formData.field_type}
                  onValueChange={(value) => setFormData({ ...formData, field_type: value as FieldType })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FIELD_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="unit_of_measure">Unit of Measure</Label>
                <Input
                  id="unit_of_measure"
                  value={formData.unit_of_measure}
                  onChange={(e) => setFormData({ ...formData, unit_of_measure: e.target.value })}
                  placeholder="e.g., lbs, gsm, inches"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="help_text">Help Text</Label>
              <Textarea
                id="help_text"
                value={formData.help_text}
                onChange={(e) => setFormData({ ...formData, help_text: e.target.value })}
                placeholder="Brief description to help users fill this field"
                rows={2}
              />
            </div>

            {/* Dropdown/Multiselect Options Input */}
            {(formData.field_type === 'dropdown' || formData.field_type === 'multiselect') && (
              <div className="space-y-3">
                <Label>Available Options *</Label>
                <div className="space-y-2">
                  {allowedValues.map((option, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <Input value={option} disabled className="flex-1" />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeOption(index)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>

                <div className="flex gap-2">
                  <Input
                    value={newOptionInput}
                    onChange={(e) => setNewOptionInput(e.target.value)}
                    placeholder="Enter option and press Add"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addOption();
                      }
                    }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={addOption}
                    disabled={!newOptionInput.trim()}
                  >
                    Add
                  </Button>
                </div>

                {allowedValues.length === 0 && (
                  <p className="text-sm text-muted-foreground">
                    No options added yet. Add at least one option.
                  </p>
                )}
              </div>
            )}

            <div>
              <Label htmlFor="default_value">Default Value</Label>
              <Input
                id="default_value"
                value={formData.default_value}
                onChange={(e) => setFormData({ ...formData, default_value: e.target.value })}
                placeholder="Optional default value"
              />
            </div>

            <div>
              <Label htmlFor="display_order">Display Order</Label>
              <Input
                id="display_order"
                type="number"
                value={formData.display_order}
                onChange={(e) =>
                  setFormData({ ...formData, display_order: parseInt(e.target.value) || 1 })
                }
              />
            </div>

            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <Switch
                  id="is_required"
                  checked={formData.is_required}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, is_required: checked })
                  }
                />
                <Label htmlFor="is_required">Required</Label>
              </div>

              {editingField && (
                <div className="flex items-center space-x-2">
                  <Switch
                    id="is_active"
                    checked={formData.is_active}
                    onCheckedChange={(checked) =>
                      setFormData({ ...formData, is_active: checked })
                    }
                  />
                  <Label htmlFor="is_active">Active</Label>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCloseDialog}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={
                !formData.category_id || 
                !formData.field_name || 
                !formData.field_label ||
                ((formData.field_type === 'dropdown' || formData.field_type === 'multiselect') && allowedValues.length === 0)
              }
            >
              {editingField ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Field Definition?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this field definition. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
